import type { IntlShape } from 'react-intl';
import type { ChartData } from '../catalog/types';

export type DonutLegendItem = Readonly<{
  label: string;
  count: number;
  percentLabel: string;
  color: string;
}>;

export function formatChartLegendPercent(
  intl: IntlShape,
  value: number,
  total: number
): string {
  if (total <= 0) {
    return intl.formatNumber(0, {
      style: 'percent',
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
    });
  }

  return intl.formatNumber(value / total, {
    style: 'percent',
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  });
}

export function formatDonutSliceTooltipValue(
  intl: IntlShape,
  value: number,
  total: number
): string {
  return `${Math.floor(value)} (${formatChartLegendPercent(intl, value, total)})`;
}

export function formatDonutSliceTooltip(
  intl: IntlShape,
  name: string,
  value: number,
  total: number
): string {
  return `${name}: ${formatDonutSliceTooltipValue(intl, value, total)}`;
}

export function buildDonutLegendItems(
  data: ChartData,
  colors: readonly string[],
  intl: IntlShape
): DonutLegendItem[] {
  const total = getDonutTotalValue(data);

  return data.values.map((value, index) => {
    const name = data.labels[index] ?? String(index);

    return {
      label: name,
      count: Math.floor(value),
      percentLabel: formatChartLegendPercent(intl, value, total),
      color: colors[index % colors.length] ?? colors[0] ?? '#000000',
    };
  });
}

export function getDonutTotalValue(data: ChartData): number {
  return data.values.reduce((sum, value) => sum + value, 0);
}
